import React from 'react';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import {
    DataStructure,
    getParentDataObject
} from '~/shared/src/utilities/dataStructure';
import dataUtilities from '~/shared/src/utilities/dataUtils';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';

import {
    ColumnSortMenuColumnSorter,
    ColumnSortMenuSingleColumn
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {
    ColumnConfigMenuColumn,
    ColumnConfigMenuColumnsGroup,
    ColumnConfigMenuSingleColumn
} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import {
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getItemTypeDisplayName,
    getPropertyKeyDisplayName,
    getPropertySetDisplayName,
    getPropertyValueDisplayName,
    getReferenceSetDisplayNameFromItemType
} from '~/frontend/src/utilities/renderHelpers';


export type ColumnStyleProps = {
    align? : 'center' | 'left' | 'right';
    resizeable? : boolean;
    style? : React.CSSProperties;
    width? : number | 'auto';
    minWidth? : number;
    maxWidth? : number;
}


/**
 *
 * @param columnDefinition
 * @param dataStructure
 * @param schema
 * @param rawMode
 * @param showAdditionalRawNames
 */
export const getColumnHeader = ( columnDefinition : ColumnDefinition, dataStructure : DataStructure, schema : Schema, rawMode : boolean, showAdditionalRawNames : boolean ) : string => {
    const dataDescriptor = columnDefinition.dataDescriptor,
          dataObject     = dataDescriptor.dataObject;

    let retVal = __( columnDefinition.header );

    if ( dataObject ) {
        if ( columnDefinition.header === 'item.key' ) {
            retVal = getItemKeyDisplayName( dataObject.item.objectType ?? 0, schema, rawMode, showAdditionalRawNames );
        }
        else if ( columnDefinition.header === 'item.name' ) {
            retVal = getItemNameDisplayName( dataObject.item.objectType ?? 0, schema, rawMode, showAdditionalRawNames );
        }
        else if ( columnDefinition.header === 'item.type' ) {
            retVal = getItemTypeDisplayName( dataObject.item.objectType ?? 0, schema, rawMode, showAdditionalRawNames );
        }
        else if (
            ( columnDefinition.header === 'property.type' || columnDefinition.header === 'property.type.orderNum' || columnDefinition.header === 'property.type.key' || columnDefinition.header === 'property.type.value' )
            && dataObject.item.objectType && dataDescriptor.propertyTypeName
        ) {
            const propertySetId = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( dataObject.item.objectType, dataDescriptor.propertyTypeName )?.id;

            if ( propertySetId ) {
                retVal = getPropertySetDisplayName( propertySetId, schema, rawMode, showAdditionalRawNames );

                if ( columnDefinition.header === 'property.type.orderNum' ) {
                    retVal = `${retVal}: ${__( 'YN.base.finder.detail.properties.orderNum' )}`;
                }
                else if ( columnDefinition.header === 'property.type.key' ) {
                    retVal = `${retVal}: ${getPropertyKeyDisplayName( propertySetId, schema, rawMode, showAdditionalRawNames )}`;
                }
                else if ( columnDefinition.header === 'property.type.value' ) {
                    retVal = `${retVal}: ${getPropertyValueDisplayName( propertySetId, schema, rawMode, showAdditionalRawNames )}`;
                }
            }

        }
        else if ( columnDefinition.header === 'reference.type' && dataObject.item.objectType && dataObject.reference?.referenceConfiguration ) {
            const referenceTypes         = Object.keys( dataObject.reference.referenceConfiguration ) as string[],
                  referenceType          = referenceTypes[ 0 ],
                  referenceConfiguration = dataObject.reference.referenceConfiguration[ referenceType ],
                  parentDataObject       = getParentDataObject( dataObject.id, dataStructure )

            if ( referenceConfiguration.referenceDirection && parentDataObject?.item.objectType ) {
                const referenceDirection = ( referenceConfiguration.referenceDirection === 'SrcToDest' ? 'src' : 'dest' ) as 'dest' | 'src',
                      referenceSetId     = metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType( dataObject.item.objectType, parentDataObject.item.objectType, referenceType, referenceDirection )[ 0 ]?.id;

                if ( referenceSetId ) {
                    retVal = getReferenceSetDisplayNameFromItemType( referenceSetId, dataObject.item.objectType, schema, rawMode, showAdditionalRawNames, referenceDirection );
                }
            }
        }
    }

    return retVal;
}

/**
 * Gets sorters with their field.
 *
 * @param {ColumnSorter[]} sorters
 *
 * @return {ColumnSortMenuColumnSorter[]}
 */
export const convertSortersToSortersWithField = ( sorters : ColumnSorter[] ) : ColumnSortMenuColumnSorter[] => {
    const retVal : ColumnSortMenuColumnSorter[] = [];

    for ( const sorter of sorters ) {
        const sorterWithField : ColumnSortMenuColumnSorter = {
            ...sorter
        };

        retVal.push( sorterWithField );
    }

    return retVal;
}

/**
 * Gets sorters.
 *
 * @param {ColumnSortMenuColumnSorter[]} sortersWithField
 * @param {ColumnDefinition[]} columnDefinitions
 *
 * @return {ColumnSorter[]}
 */
export const convertSortersWithFieldToSorters = ( sortersWithField : ColumnSortMenuColumnSorter[], columnDefinitions : ColumnDefinition[] ) : ColumnSorter[] => {
    const retVal : ColumnSorter[] = [];

    for ( const sorterWithField of sortersWithField ) {
        let column;

        for ( const columnDefinition of columnDefinitions ) {
            if ( columnDefinition.field === sorterWithField.field ) {
                column = columnDefinition;
                break;
            }
        }

        if ( column ) {
            retVal.push( {
                key      : column.key,
                field    : column.field,
                direction: sorterWithField.direction
            } );
        }
    }

    return retVal;
}

/**
 * Extracts and returns the config attributes from the given column definition.
 *
 * @param {Object} columnDefinition
 *
 * @return {Object}
 */
export const getColumnConfigFromColumnDefinition = ( columnDefinition : ColumnConfig & Object ) : ColumnConfig => {
    const retVal : ColumnConfig = {
        key  : columnDefinition.key,
        field: columnDefinition.field
    };

    if ( columnDefinition ) {
        if ( columnDefinition.width !== undefined && columnDefinition.width !== 0 ) {
            retVal.width = columnDefinition.width;
        }

        if ( columnDefinition.resizeable !== undefined ) {
            retVal.resizeable = columnDefinition.resizeable;
        }

        if ( columnDefinition.hidden !== undefined ) {
            retVal.hidden = columnDefinition.hidden;
        }
    }

    return retVal;
}

/**
 *
 * @param props
 */
export const processColumnStyle = function( props : ColumnStyleProps & object ) : React.CSSProperties {
    const columnStyle = Object.assign( {}, props.style ?? {} );

    // align
    columnStyle.textAlign = props.align;

    // width
    if ( !props.width || props.width === 'auto' ) {
        columnStyle.width = 'auto';
        columnStyle.minWidth = '150px';
    }
    else if ( props.width ) {
        columnStyle.width = props.width + 'px';

        if ( !props.style?.flex ) {
            columnStyle.flex = 'none';
        }
    }

    // minWidth
    if ( props.minWidth ) {
        if ( columnStyle.width !== 'auto' && props.width && props.width < props.minWidth ) {
            columnStyle.width = props.minWidth + 'px';

            if ( !props.style?.flex ) {
                columnStyle.flex = 'none';
            }
        }

        columnStyle.minWidth = props.minWidth + 'px';
    }
    if ( !columnStyle.minWidth ) {
        columnStyle.minWidth = 'none';
    }

    // maxWidth
    if ( props.maxWidth ) {
        if ( columnStyle.width !== 'auto' && props.width && props.width > props.maxWidth ) {
            columnStyle.width = props.maxWidth + 'px';

            if ( !props.style?.flex ) {
                columnStyle.flex = 'none';
            }
        }

        columnStyle.maxWidth = props.maxWidth + 'px';
    }
    else if ( props.width !== 'auto' ) {
        columnStyle.maxWidth = props.width + 'px';
    }
    if ( !columnStyle.maxWidth ) {
        columnStyle.maxWidth = 'none';
    }

    return columnStyle;
}

/**
 * Builds the grouped columns with visibility.
 *
 * @param {ColumnDefinition[]} columnDefinitions
 * @param {ColumnConfig[]} columns
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 * @param {boolean} rawMode
 * @param {boolean} showAdditionalRawNames
 *
 * @return {ColumnSortMenuSingleColumn[]}
 */
export const getAllColumns = ( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, rawMode : boolean, showAdditionalRawNames : boolean ) : ColumnSortMenuSingleColumn[] => {
    const retVal : ColumnSortMenuSingleColumn[] = [];

    for ( const columnDefinition of columnDefinitions ) {
        const column = columns.find( colConfig => colConfig.field === columnDefinition.field );

        retVal.push( {
            key      : columnDefinition.key,
            field    : columnDefinition.field,
            header   : getColumnHeader( columnDefinition, dataStructure, schema, rawMode, showAdditionalRawNames ),
            hidden   : !column,
            hidable  : columnDefinition.hidable ?? true,
            valueType: dataUtilities.getDataValueType( columnDefinition.dataDescriptor, schema, dataStructure ) ?? 'STRING'
        } );
    }

    return retVal;
}

/**
 * Applies the visibility given by allColumnsGrouped to columns by adding or removing appropriate columns.
 *
 * @param {ColumnConfigMenuColumn[]} allColumnsGrouped
 * @param {ColumnDefinition[]} columnDefinitions
 * @param {ColumnConfig[]} [columns]
 * @param {String} schema
 * @param {String} itemTypeName
 *
 * @return {ColumnConfig[]}
 */
export const applyAllColumnsToColumns = ( allColumnsGrouped : ColumnConfigMenuColumn[], columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], insertPosition : 'first' | 'defined' | 'last' = 'defined' ) : ColumnConfig[] => {
    const hasColumnGroups = ( allColumnsGrouped?.[ 0 ] as unknown as ColumnConfigMenuColumnsGroup )?.columns !== undefined;

    let retVal : ColumnConfig[] = ( [] as ColumnConfig[] ).concat( columns );

    if ( hasColumnGroups ) {
        const columnGroups = ( [] as ColumnConfigMenuColumnsGroup[] ).concat( allColumnsGrouped as ColumnConfigMenuColumnsGroup[] );

        // iterate column groups
        for ( let i = columnGroups.length - 1; i >= 0; i-- ) {
            const groupColumns = columnGroups[ i ].columns;

            // iterate columns
            for ( let k = 0; k < groupColumns.length; k++ ) {
                const column           = groupColumns[ k ],
                      columnDefinition = columnDefinitions.find( colDef => colDef.field === column.field ),
                      columnConfig     = columns.find( colConfig => colConfig.field === column.field );

                if ( columnDefinition ) {

                    // add column
                    if ( !column.hidden && !columnConfig ) {
                        retVal = addColumnToColumns( { ...columnDefinition, hidden: false }, retVal, columnDefinitions, insertPosition === 'first' ? 0 : ( insertPosition === 'last' ? retVal.length : undefined ) );
                    }

                    // remove column
                    else if ( column.hidden && columnConfig ) {
                        retVal = removeColumnFromColumns( columnDefinition, retVal );
                    }
                }
            }
        }
    }
    else {
        const allColumns = ( [] as ColumnConfigMenuSingleColumn[] ).concat( allColumnsGrouped as ColumnConfigMenuSingleColumn[] );

        // iterate columns
        for ( let i = 0; i < allColumns.length; i++ ) {
            const column           = allColumns[ i ],
                  columnDefinition = columnDefinitions.find( colDef => colDef.field === column.field ),
                  columnConfig     = columns.find( colConfig => colConfig.field === column.field );

            if ( columnDefinition ) {

                // add column
                if ( !column.hidden && !columnConfig ) {
                    retVal = addColumnToColumns( { ...columnDefinition, hidden: false }, retVal, columnDefinitions, insertPosition === 'first' ? 0 : ( insertPosition === 'last' ? retVal.length : undefined ) );
                }

                // remove column
                else if ( column.hidden && columnConfig ) {
                    retVal = removeColumnFromColumns( columnDefinition, retVal );
                }
            }
        }
    }

    return retVal;
}

/**
 * Adds the given columnDefinition to the given columns.
 *
 * @param {ColumnDefinition} columnDefinition
 * @param {ColumnConfig[]} columns
 * @param {ColumnDefinition[]} [columnDefinitions=[]]
 * @param {number} [index=-1]           The index to insert the columnDefinition into columns at or -1 to insert depending on columnDefinitions index.
 *
 * @return {ColumnConfig[]}
 */
export const addColumnToColumns = ( columnDefinition : ColumnDefinition, columns : ColumnConfig[], columnDefinitions : ColumnDefinition[] = [], index : number = -1 ) : ColumnConfig[] => {
    let retVal : ColumnConfig[] = columns;

    // if column not in columns ...
    if ( !columns.find( colConfig => colConfig.field === columnDefinition.field ) ) {
        retVal = ( [] as ColumnConfig[] ).concat( retVal );

        let insertIndex = 0;

        if ( index !== -1 ) {
            insertIndex = Math.min( index, retVal.length );
        }
        else if ( columnDefinitions.length === 0 ) {
            insertIndex = retVal.length;
        }
        else {
            for ( let i = 0; i < columnDefinitions.length; i++ ) {
                if ( columns.find( colConfig => colConfig.field === columnDefinitions[ i ].field ) ) {
                    insertIndex++;
                }
                if ( columnDefinitions[ i ].field === columnDefinition.field ) {
                    break;
                }
            }
        }

        retVal.splice( insertIndex, 0, getColumnConfigFromColumnDefinition( columnDefinition ) );
    }

    return retVal;
}

/**
 * Removes the given columnDefinition from the given columns.
 *
 * @param {ColumnConfig[]} columns
 * @param {ColumnDefinition} columnDefinition
 *
 * @return {ColumnConfig[]}
 */
export const removeColumnFromColumns = ( columnDefinition : ColumnDefinition, columns : ColumnConfig[] ) : ColumnConfig[] => {
    let retVal : ColumnConfig[] = columns;

    for ( let index = 0; index < retVal.length; index++ ) {
        if ( retVal[ index ].field === columnDefinition.field ) {
            retVal = ( [] as ColumnConfig[] ).concat( retVal );
            retVal.splice( index, 1 );
            break;
        }
    }

    return retVal;
}
